/**
 * @module api/questionnaire
 * @description API client for questionnaire submission endpoints.
 */

import axios from "axios";
import { API_BASE_URL, ENDPOINTS } from "../config/apiConfig";

const EMPTY_ASSIGNED_MODE_TOKEN = "__EMPTY__";

/**
 * Submit the starting questionnaire (demographics + ATI + English + TIPI).
 * @param {Object} body - { participantCode, data: StartingQuestionnaireData }
 * @returns {Promise<void>}
 */
export const submitStartingQuestionnaire = async (body) => {
  try {
    await axios.post(`${API_BASE_URL}${ENDPOINTS.STARTING_QUESTIONNAIRE}`, body);
  } catch (error) {
    console.error("[API] Starting questionnaire submission failed:", error);
  }
};

/**
 * Submit the ending questionnaire (SUS + open-ended).
 * @param {Object} body - { participantCode, assignedMode, data }
 * @returns {Promise<void>}
 */
export const submitEndingQuestionnaire = async (body) => {
  try {
    await axios.post(`${API_BASE_URL}${ENDPOINTS.ENDING_QUESTIONNAIRE}`, body);
  } catch (error) {
    console.error("[API] Ending questionnaire submission failed:", error);
  }
};

/**
 * Submit the after-phase questionnaire (TPA).
 * @param {Object} body - { participantCode, assignedMode, data }
 * @returns {Promise<void>}
 */
export const submitAfterPhaseQuestionnaire = async (body) => {
  try {
    await axios.post(`${API_BASE_URL}${ENDPOINTS.AFTER_PHASE}`, body);
  } catch (error) {
    console.error("[API] After-phase questionnaire submission failed:", error);
  }
};

/**
 * Submit the after-image questionnaire (caption quality).
 * @param {Object} body - { participantCode, imageurl, assignedMode, data }
 * @returns {Promise<void>}
 */
export const submitAfterImageQuestionnaire = async (body) => {
  try {
    await axios.post(`${API_BASE_URL}${ENDPOINTS.AFTER_IMAGE}`, body);
  } catch (error) {
    console.error("[API] After-image questionnaire submission failed:", error);
  }
};

/**
 * Submit an activity log entry.
 * @param {Object} body - { participantCode, logs: LogData }
 * @returns {Promise<void>}
 */
export const submitlogs = async (body) => {
  try {
    await axios.post(`${API_BASE_URL}${ENDPOINTS.LOG}`, body);
  } catch (error) {
    console.error("[API] Log submission failed:", error);
  }
};

export const getAdminParticipants = async (adminToken) => {
  const response = await axios.get(
    `${API_BASE_URL}${ENDPOINTS.ADMIN_PARTICIPANTS}`,
    {
      headers: {
        "x-admin-token": adminToken,
      },
    }
  );

  return response.data;
};

export const getAdminQuestionnaires = async (assignedMode, participantCode, adminToken) => {
  const encodedAssignedMode = assignedMode
    ? encodeURIComponent(assignedMode)
    : EMPTY_ASSIGNED_MODE_TOKEN;

  const response = await axios.get(
    `${API_BASE_URL}${ENDPOINTS.ADMIN_QUESTIONNAIRES}/${encodedAssignedMode}/${encodeURIComponent(participantCode)}`,
    {
      headers: {
        "x-admin-token": adminToken,
      },
    }
  );

  return response.data;
};

export const getAdminSummary = async (adminToken, prolificOnly = false) => {
  const response = await axios.get(
    `${API_BASE_URL}${ENDPOINTS.ADMIN_SUMMARY}${prolificOnly ? "?prolificOnly=1" : ""}`,
    {
      headers: {
        "x-admin-token": adminToken,
      },
    }
  );

  return response.data;
};

export const getAdminActivity = async (assignedMode, participantCode, adminToken) => {
  const encodedAssignedMode = assignedMode
    ? encodeURIComponent(assignedMode)
    : EMPTY_ASSIGNED_MODE_TOKEN;

  const response = await axios.get(
    `${API_BASE_URL}${ENDPOINTS.ADMIN_ACTIVITY}/${encodedAssignedMode}/${encodeURIComponent(participantCode)}`,
    {
      headers: {
        "x-admin-token": adminToken,
      },
    }
  );

  return response.data;
};
